import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import Keycloak from 'keycloak-js';

import router from './router';
import store from './store/index';

interface AxiosInstances {
  axiosInstance: AxiosInstance;
  axiosInstanceFileUpload: AxiosInstance;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

const csrfCookie = Cookies.get('csrftoken');
const baseURL = !import.meta.env.PROD ? 'http://localhost' : document.location.origin;

function applyInterceptors(instance: AxiosInstance, keycloak: Keycloak): void {
  instance.interceptors.request.use(
    async function (config: InternalAxiosRequestConfig) {
      if (router.currentRoute.value.meta.requireLogin !== false) {
        if (!keycloak.authenticated) {
          // If user is not logged in
          setAuthState(keycloak);
          if (keycloak.refreshToken === undefined) {
            performLogin(keycloak, window.location.pathname);
          }
        }
        await refresh_keycloak_token(keycloak);
        const token = keycloak.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          if (config.headers) {
            delete config.headers.Authorization;
          }
        }
        return config;
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );
}

export function createAxiosInstance(keycloak: Keycloak): AxiosInstances {
  let token = keycloak.token;
  if (token) {
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
  } else {
    axios.defaults.headers.common['Authorization'] = '';
  }

  const axiosInstance = axios.create({
    baseURL: baseURL,
    headers: {
      Authorization: 'Bearer ' + token,
    },
    withCredentials: true,
  });
  applyInterceptors(axiosInstance, keycloak);

  // This was added to help with large uploads -> See Content-Type
  const axiosInstanceFileUpload = axios.create({
    baseURL: baseURL,
    headers: {
      Authorization: 'Bearer ' + token,
      'X-CSRFToken': csrfCookie,
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  });
  applyInterceptors(axiosInstanceFileUpload, keycloak);

  return { axiosInstance, axiosInstanceFileUpload };
}

export function isTokenExpired(token: string): boolean {
  const decode = jwt_decode<DecodedToken>(token);
  return dayjs.unix(decode.exp).diff(dayjs()) < 1;
}

function performLogin(keycloak: Keycloak, destination: string): void {
  const getUrl = window.location;
  let redirect = getUrl.protocol + '//' + getUrl.host;
  if (destination !== '/') {
    redirect = redirect + '?r=' + destination;
  }
  keycloak.login({ redirectUri: redirect });
}

export async function refresh_keycloak_token(keycloak: Keycloak): Promise<Keycloak> {
  keycloak
    .updateToken(Number(import.meta.env.VITE_TIME_BEFORE_TOKEN_REFRESH))
    .then(function (refreshed) {
      if (refreshed) {
        setAuthState(keycloak);
      }
    })
    .catch(err => {
      console.error(err);
      // If the token is undefined, it might be expired
      if (keycloak.token === undefined) {
        keycloak.clearToken();
        router.push('/login');
      } else {
        keycloak.clearToken();
        router.push('/something-went-wrong');
      }
    });
  return keycloak;
}

function setAuthState(keycloak: Keycloak): void {
  store.commit('setAuthenticated', keycloak.authenticated);
}
